import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, SignOut, MagnifyingGlass, ArrowClockwise,
  LockKey, Buildings, EnvelopeSimple, Phone, MapPin, Sparkle
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "invoquity_admin_passcode";

export default function AdminDashboard() {
  const [passcode, setPasscode] = useState(() => localStorage.getItem(LS_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(LS_KEY);
    if (!stored) return;
    (async () => {
      try {
        await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Passcode": stored } });
        setAuthed(true);
      } catch {
        localStorage.removeItem(LS_KEY);
      }
    })();
  }, []);

  const login = async (e) => {
    e.preventDefault();
    if (!passcode.trim()) return;
    setVerifying(true);
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Passcode": passcode } });
      localStorage.setItem(LS_KEY, passcode);
      setAuthed(true);
      toast.success("Welcome back.");
    } catch {
      toast.error("Invalid passcode.");
    } finally {
      setVerifying(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(LS_KEY);
    setPasscode("");
    setAuthed(false);
  };

  if (!authed) {
    return <LoginScreen passcode={passcode} setPasscode={setPasscode} onSubmit={login} loading={verifying} />;
  }
  return <Dashboard passcode={passcode} onLogout={logout} />;
}

function LoginScreen({ passcode, setPasscode, onSubmit, loading }) {
  return (
    <main data-testid="admin-login" className="min-h-screen grid place-items-center bg-slate-50 p-6">
      <motion.form
        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-3xl bg-white border border-slate-100 shadow-soft p-8"
      >
        <a href="/" data-testid="admin-back-home" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800">
          <ArrowLeft size={14} /> Back to site
        </a>
        <div className="mt-4 flex items-center gap-2">
          <img src="/invoquity-logo.png" alt="Invoquity" className="h-7 w-auto" />
          <span className="font-display text-sm font-medium text-slate-500">/ Admin</span>
        </div>
        <h1 className="mt-6 font-display text-2xl font-semibold text-slate-900">Enter admin passcode</h1>
        <p className="mt-1.5 text-sm text-slate-500">Restricted to Invoquity team members.</p>

        <label htmlFor="passcode" className="mt-6 block text-xs font-semibold uppercase tracking-widest text-slate-600">Passcode</label>
        <div className="mt-2 relative">
          <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            id="passcode"
            data-testid="admin-passcode-input"
            type="password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            className="h-12 rounded-xl pl-9"
            placeholder="••••••••••••"
            autoComplete="current-password"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-btn"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-emerald-700 to-emerald-800 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:shadow-glow-blue transition-all disabled:opacity-60"
        >
          {loading ? "Verifying…" : "Sign in"}
        </button>
      </motion.form>
    </main>
  );
}

function Dashboard({ passcode, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_industry: [], by_revenue: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const headers = { "X-Admin-Passcode": passcode };
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads?limit=500`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      toast.error("Failed to load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return leads;
    return leads.filter((l) => [l.company_name, l.owner_name, l.email, l.city, l.industry, l.mobile]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(term)));
  }, [q, leads]);

  const exportCSV = () => {
    if (!leads.length) return toast.info("Nothing to export yet.");
    const cols = ["created_at","company_name","owner_name","email","mobile","city","industry","annual_revenue","monthly_invoice_value","gst_number","website","message"];
    const escape = (v) => `"${String(v ?? "").replaceAll('"','""')}"`;
    const lines = [cols.join(","), ...leads.map((l) => cols.map((c) => escape(l[c])).join(","))];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `invoquity-leads-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-slate-50">
      {/* header */}
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="inline-flex items-center gap-2">
              <img src="/invoquity-logo.png" alt="Invoquity" className="h-7 w-auto" />
              <span className="font-display text-sm font-medium text-slate-400">/ Admin</span>
            </a>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} data-testid="admin-refresh-btn" className="rounded-full">
              <ArrowClockwise size={14} className="mr-1.5" /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={exportCSV} data-testid="admin-export-csv-btn" className="rounded-full">
              Export CSV
            </Button>
            <Button variant="ghost" size="sm" onClick={onLogout} data-testid="admin-logout-btn" className="rounded-full">
              <SignOut size={14} className="mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* KPI cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI title="Total leads" value={stats.total} icon={Sparkle} />
          <KPI title="Top industry" value={stats.by_industry?.[0]?.industry || "—"} icon={Buildings} sub={stats.by_industry?.[0]?.count ? `${stats.by_industry[0].count} leads` : ""} />
          <KPI title="Top revenue bucket" value={stats.by_revenue?.[0]?.bucket || "—"} icon={Sparkle} sub={stats.by_revenue?.[0]?.count ? `${stats.by_revenue[0].count} leads` : ""} />
          <KPI title="This week" value={leads.filter((l) => within7Days(l.created_at)).length} icon={Sparkle} />
        </div>

        {/* table */}
        <div className="mt-8 rounded-3xl bg-white border border-slate-100 shadow-soft">
          <div className="flex items-center justify-between gap-3 p-5 border-b border-slate-100">
            <h2 className="font-display text-lg font-semibold">Early Access Leads</h2>
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="admin-search-input" placeholder="Search leads…" value={q} onChange={(e) => setQ(e.target.value)}
                     className="h-10 w-64 rounded-full pl-9" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-leads-table">
              <thead className="text-left text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-5 py-3">Received</th>
                  <th className="px-5 py-3">Company</th>
                  <th className="px-5 py-3">Owner</th>
                  <th className="px-5 py-3">Contact</th>
                  <th className="px-5 py-3">Industry</th>
                  <th className="px-5 py-3">Revenue</th>
                  <th className="px-5 py-3">Monthly invoices</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-400">Loading…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-400" data-testid="admin-empty-state">No leads yet.</td></tr>
                )}
                {filtered.map((l, i) => (
                  <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50/70" data-testid={`admin-lead-row-${i}`}>
                    <td className="px-5 py-3 text-slate-500 whitespace-nowrap">{fmt(l.created_at)}</td>
                    <td className="px-5 py-3 font-medium text-slate-900">{l.company_name}</td>
                    <td className="px-5 py-3 text-slate-700">{l.owner_name}</td>
                    <td className="px-5 py-3 text-slate-700">
                      <div className="flex flex-col">
                        <span>{l.email}</span>
                        <span className="text-slate-500 text-xs">{l.mobile}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3"><span className="rounded-full bg-emerald-50 text-emerald-800 px-2.5 py-1 text-xs font-medium">{l.industry}</span></td>
                    <td className="px-5 py-3 text-slate-700 whitespace-nowrap">{l.annual_revenue}</td>
                    <td className="px-5 py-3 text-slate-700 whitespace-nowrap">{l.monthly_invoice_value}</td>
                    <td className="px-5 py-3 text-right">
                      <button data-testid={`admin-view-lead-${i}`} onClick={() => setSelected(l)}
                              className="text-emerald-800 text-xs font-semibold hover:underline">View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selected && <LeadDrawer lead={selected} onClose={() => setSelected(null)} />}
    </main>
  );
}

function KPI({ title, value, sub, icon: Icon }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-100 shadow-soft p-6">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">{title}</p>
        <span className="grid place-items-center w-8 h-8 rounded-lg bg-emerald-50 text-emerald-800"><Icon size={16} weight="duotone" /></span>
      </div>
      <p className="mt-4 font-display text-3xl font-semibold text-slate-900 truncate">{value}</p>
      {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

function LeadDrawer({ lead, onClose }) {
  return (
    <div className="fixed inset-0 z-50" data-testid="admin-lead-drawer">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} />
      <motion.aside
        initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.25 }}
        className="absolute right-0 top-0 h-full w-full sm:max-w-md bg-white border-l border-slate-200 shadow-2xl overflow-y-auto"
      >
        <div className="p-6 border-b border-slate-100 flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500">Lead</p>
            <h3 className="font-display text-xl font-semibold text-slate-900 mt-1">{lead.company_name}</h3>
            <p className="text-sm text-slate-500 mt-0.5">{fmt(lead.created_at)}</p>
          </div>
          <button onClick={onClose} data-testid="admin-drawer-close" className="text-slate-400 hover:text-slate-800 text-2xl leading-none">×</button>
        </div>
        <div className="p-6 space-y-5 text-sm">
          <Row icon={Buildings} label="Owner" value={lead.owner_name} />
          <Row icon={EnvelopeSimple} label="Email" value={lead.email} />
          <Row icon={Phone} label="Mobile" value={lead.mobile} />
          <Row icon={MapPin} label="City" value={lead.city} />
          <div className="grid grid-cols-2 gap-4">
            <Cell label="Industry" value={lead.industry} />
            <Cell label="Annual Revenue" value={lead.annual_revenue} />
            <Cell label="Monthly invoice value" value={lead.monthly_invoice_value} />
            <Cell label="GST" value={lead.gst_number || "—"} />
            <Cell label="Website" value={lead.website || "—"} />
            <Cell label="Consent" value={lead.consent ? "Yes" : "No"} />
          </div>
          {lead.message && (
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">Message</p>
              <p className="text-slate-700 leading-relaxed whitespace-pre-wrap rounded-xl bg-slate-50 p-4 border border-slate-100">{lead.message}</p>
            </div>
          )}
          <div className="pt-4 border-t border-slate-100 flex gap-2">
            <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 text-white px-4 py-2 text-xs font-semibold">
              <EnvelopeSimple size={14} /> Email
            </a>
            {lead.mobile && (
              <a href={`tel:${lead.mobile}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 text-slate-800 px-4 py-2 text-xs font-semibold">
                <Phone size={14} /> Call
              </a>
            )}
          </div>
        </div>
      </motion.aside>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="grid place-items-center w-8 h-8 rounded-lg bg-slate-100 text-slate-600 shrink-0"><Icon size={14} weight="duotone" /></span>
      <div>
        <p className="text-xs uppercase tracking-widest text-slate-500">{label}</p>
        <p className="text-slate-800">{value}</p>
      </div>
    </div>
  );
}

function Cell({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-slate-500">{label}</p>
      <p className="text-slate-800 mt-0.5 break-words">{value}</p>
    </div>
  );
}

function fmt(d) {
  try { return new Date(d).toLocaleString(); } catch { return String(d); }
}
function within7Days(d) {
  try { return (Date.now() - new Date(d).getTime()) <= 7 * 86400000; } catch { return false; }
}
